import { context, propagation } from '@opentelemetry/api';
import { endpoints } from './endpoints.js';
import { HubClientError, parseHubErrorBody } from './errors.js';
import { createSessionRefresher, withSessionRefresh } from './auth/session-refresh.js';
export { HubClientError } from './errors.js';
export class HubClient {
    baseUrl;
    apiKey;
    credentials;
    autoRefresh;
    authToken;
    // `autoRefresh` composition: a 401 triggers one deduped POST /auth/refresh
    // and a single replay (see auth/session-refresh.ts).
    fetchWithRefresh;
    constructor(options = {}) {
        const baseUrl = options.baseUrl ?? process.env.TF_HUB_URL;
        if (!baseUrl) {
            throw new Error('HubClient: baseUrl not provided and TF_HUB_URL env var is not set');
        }
        this.baseUrl = baseUrl.replace(/\/+$/, '');
        this.apiKey = options.apiKey ?? process.env.TF_API_KEY;
        this.credentials = options.credentials;
        this.autoRefresh = options.autoRefresh ?? false;
        this.authToken = options.authToken;
        const plainFetch = (input, init) => fetch(input, init);
        this.fetchWithRefresh = this.autoRefresh
            ? withSessionRefresh(plainFetch, createSessionRefresher({ hubBaseUrl: this.baseUrl, credentials: this.credentials }))
            : plainFetch;
        this._exposeEndpoints(endpoints);
    }
    /**
     * Return a lightweight clone bound to `jwt` — same `baseUrl`/`apiKey`/
     * `credentials`/`autoRefresh`, with `authToken = jwt`. Does NOT mutate the
     * receiver, so a request handler can `const asUser = hub.withAuth(token)` and
     * make several per-user-scoped calls without disturbing the shared machine
     * client. See HUB-024 / HUB-040.
     */
    withAuth(jwt) {
        return new HubClient({
            baseUrl: this.baseUrl,
            apiKey: this.apiKey,
            credentials: this.credentials,
            autoRefresh: this.autoRefresh,
            authToken: jwt,
        });
    }
    _exposeEndpoints(defs) {
        const self = this;
        for (const [name, def] of Object.entries(defs)) {
            self[name] = (args) => this._call(name, def, args);
        }
    }
    async _call(name, def, args) {
        validateArgs(name, def, args);
        let path = def.path;
        if (args?.params) {
            for (const [k, v] of Object.entries(args.params)) {
                path = path.replace(`:${k}`, encodeURIComponent(String(v)));
            }
        }
        const url = new URL(this.baseUrl + path);
        if (args?.query) {
            for (const [k, v] of Object.entries(args.query)) {
                if (v !== undefined)
                    url.searchParams.set(k, String(v));
            }
        }
        const headers = {};
        const init = { method: def.method, headers };
        if (this.credentials) {
            init.credentials = this.credentials;
        }
        if (args?.body !== undefined) {
            if (def.bodyKind === 'multipart' || isPassthroughBody(args.body)) {
                // FormData/Blob/stream/etc.: hand the body to `fetch` UNTOUCHED and do
                // NOT set content-type, so the runtime derives it (incl. the multipart
                // boundary). The api-key, trace-context, idempotency, and task-20
                // Authorization headers below still attach — they are independent of
                // how the body is encoded.
                init.body = args.body;
            }
            else {
                headers['content-type'] = 'application/json';
                init.body = JSON.stringify(args.body);
            }
        }
        if (args?.idempotencyKey !== undefined) {
            headers['Idempotency-Key'] = args.idempotencyKey;
        }
        // Inject W3C trace context so hub-side spans parent under the caller's active span.
        // No-op when no global propagator is configured — safe to always call.
        propagation.inject(context.active(), headers);
        if (this.apiKey) {
            headers['x-trackframe-api-key'] = this.apiKey;
        }
        // A per-call `jwt` overrides the instance `authToken`. When either is set,
        // authorize AS the user via Bearer — the hub's auth hook reads this first,
        // so it wins over the api-key header (which stays as an ignored fallback).
        const authToken = args?.jwt ?? this.authToken;
        if (authToken) {
            headers['authorization'] = `Bearer ${authToken}`;
        }
        // The refresh call itself never refreshes-and-replays: a refused rotation
        // is the answer, and replaying it could only burn the token family.
        const doFetch = name === 'authRefresh' ? (i, n) => fetch(i, n) : this.fetchWithRefresh;
        const res = await doFetch(url, init);
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw buildHubClientError(name, def, args, res, text);
        }
        // Binary endpoints declare no TypeBox `response`, so this branch MUST come
        // before the void rule below — otherwise the bytes are silently dropped.
        if (def.responseKind === 'binary') {
            return {
                bytes: new Uint8Array(await res.arrayBuffer()),
                contentType: res.headers.get('content-type') ?? 'application/octet-stream',
            };
        }
        // 204 No Content, or endpoints declared without a response schema, resolve to void.
        if (res.status === 204 || def.response === undefined) {
            return undefined;
        }
        return res.json();
    }
    /**
     * Emit a structured telemetry log to the hub. Fire-and-forget: the caller
     * should NOT await this on the user-facing critical path.
     *
     * The hub converts the payload into an OTEL log record. The current trace
     * context travels via the W3C traceparent header injected into every
     * outbound hub call, so the resulting log record is correlated with the
     * caller's active span.
     */
    async log(level, eventType, attributes = {}) {
        await this.telemetryLog({ body: { level, eventType, attributes } });
    }
}
/**
 * Bodies that must reach `fetch` UNTOUCHED so the runtime sets `Content-Type`
 * (incl. the multipart boundary for `FormData`). Mirrors the hand-rolled
 * `hub-fetch.ts` classification a track used to carry; everything else is
 * JSON-encoded.
 */
function isPassthroughBody(body) {
    if (typeof body !== 'object' || body === null)
        return false;
    return (body instanceof FormData
        || body instanceof Blob
        || body instanceof ArrayBuffer
        || body instanceof URLSearchParams
        || body instanceof ReadableStream
        || ArrayBuffer.isView(body));
}
function isObjectSchema(s) {
    return typeof s === 'object' && s !== null;
}
function describeShape(schema) {
    if (!isObjectSchema(schema) || !schema.properties)
        return '{ ... }';
    const props = Object.keys(schema.properties);
    const required = new Set(schema.required ?? []);
    if (props.length === 0)
        return '{}';
    const fields = props
        .map((p) => (required.has(p) ? p : `${p}?`))
        .slice(0, 8)
        .join(', ');
    const ellipsis = props.length > 8 ? ', …' : '';
    return `{ ${fields}${ellipsis} }`;
}
function pathParamNames(path) {
    return Array.from(path.matchAll(/:([A-Za-z0-9_]+)/g)).map((m) => m[1]);
}
function exampleCall(name, def) {
    const slots = [];
    const params = pathParamNames(def.path);
    if (params.length > 0) {
        slots.push(`params: { ${params.join(', ')} }`);
    }
    if (def.query) {
        slots.push(`query: ${describeShape(def.query)}`);
    }
    if (def.body) {
        slots.push(`body: ${describeShape(def.body)}`);
    }
    if (def.idempotencyKey === true) {
        slots.push('idempotencyKey?: string');
    }
    return slots.length === 0
        ? `client.${name}()`
        : `client.${name}({ ${slots.join(', ')} })`;
}
function validateArgs(name, def, args) {
    if (args && Object.prototype.hasOwnProperty.call(args, 'idempotencyKey')) {
        if (def.idempotencyKey !== true) {
            throw new Error(`HubClient.${name}: ${def.method} ${def.path} does not support idempotencyKey.`);
        }
        if (typeof args.idempotencyKey !== 'string') {
            throw new Error(`HubClient.${name}: invalid idempotencyKey for ${def.method} ${def.path}; expected a string.`);
        }
    }
    // Required path params declared via `:name` placeholders.
    const paramNames = pathParamNames(def.path);
    if (paramNames.length > 0) {
        const provided = args?.params ?? {};
        const missing = paramNames.filter((p) => provided[p] === undefined || provided[p] === '');
        if (missing.length > 0) {
            throw new Error(`HubClient.${name}: missing path param${missing.length === 1 ? '' : 's'} `
                + `${missing.map((p) => `\`${p}\``).join(', ')} for ${def.method} ${def.path}.\n`
                + `  Expected: ${exampleCall(name, def)}`);
        }
    }
    // Required body — declared on the endpoint but caller passed nothing.
    if (def.body && args?.body === undefined) {
        throw new Error(`HubClient.${name}: missing \`body\` for ${def.method} ${def.path}.\n`
            + `  Expected: ${exampleCall(name, def)}\n`
            + `  Hint: arguments must be wrapped in \`{ body: ... }\`, not passed directly.`);
    }
    // If a body schema declares required keys and the caller passed an object,
    // surface missing top-level keys before the round-trip.
    if (def.body && isObjectSchema(def.body) && def.body.required && def.body.required.length > 0) {
        const body = args?.body;
        if (body && typeof body === 'object' && !Array.isArray(body)) {
            const provided = body;
            const missing = def.body.required.filter((k) => provided[k] === undefined);
            if (missing.length > 0) {
                throw new Error(`HubClient.${name}: \`body\` is missing required field${missing.length === 1 ? '' : 's'} `
                    + `${missing.map((k) => `\`${k}\``).join(', ')}.\n`
                    + `  Expected: ${exampleCall(name, def)}`);
            }
        }
    }
}
/**
 * Build a structured HubClientError from a non-2xx response. The error keeps
 * the stable `code` accessible as a top-level property so UI callers (like
 * the SchedulePreviewCard error→copy switch) can dispatch on it instead of
 * stringifying the JSON and showing a generic "Something went wrong."
 *
 * The error message also embeds a Fastify-validation hint when the response
 * looks like a body-shape rejection — same hint the previous string-only
 * formatter produced — so callers reading just `err.message` still get the
 * expected-call shape.
 */
function buildHubClientError(name, def, _args, res, rawBody) {
    const parsed = parseHubErrorBody(rawBody);
    const detail = parsed.detail ?? rawBody.slice(0, 500);
    // Fastify body-validation failures look like
    //   { code: 'FST_ERR_VALIDATION', message: 'body must be ...' }.
    // Surface the expected call shape so callers don't have to grep schemas.
    const isValidationError = parsed.code === 'FST_ERR_VALIDATION'
        || (typeof detail === 'string' && /^body must be|^body\.|^querystring|^params/i.test(detail));
    const detailWithHint = isValidationError
        ? `${detail ?? ''}\n  Expected: ${exampleCall(name, def)}`
        : detail;
    return new HubClientError({
        endpoint: `HubClient.${name} ${def.method} ${def.path}`,
        httpStatus: res.status,
        statusText: res.statusText,
        code: parsed.code,
        detail: detailWithHint,
        cause: parsed.cause,
        operationPath: parsed.operationPath,
        field: parsed.field,
        violations: parsed.violations,
        body: parsed.body,
    });
}
//# sourceMappingURL=hub-client.js.map